import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent';
import { WaterHeaterWithMetrics } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils';

const WATER_HEATER_SYSTEM_IMAGE = (
  <svg
    width='61'
    height='61'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M18.6792 2.93262C15.3236 2.93262 12.6033 5.83672 12.6033 9.4191V45.9056C12.6033 48.6333 14.1804 50.9678 16.4143 51.9264C16.5784 54.0038 18.2098 55.6353 20.1982 55.6353H21.7172V58.8786H27.0337V55.6353H33.8691V58.8786H39.1856V55.6353H39.945C41.8454 55.6353 43.4197 54.1451 43.6986 52.1996C46.3415 51.496 48.2995 48.9449 48.2995 45.9056V9.4191C48.2995 5.83672 45.5792 2.93262 42.2235 2.93262H18.6792ZM29.5034 43.6153L30.4514 48.338L31.3993 43.6153C32.8257 44.054 33.8691 45.4578 33.8691 47.1218C33.8691 49.1369 32.3389 50.7704 30.4514 50.7704C28.5638 50.7704 27.0337 49.1369 27.0337 47.1218C27.0337 45.4578 28.077 44.054 29.5034 43.6153ZM23.9957 13.4732C21.8984 13.4732 20.1982 15.2882 20.1982 17.5272V39.4191H40.7045V17.5272C40.7045 15.2882 39.0043 13.4732 36.9071 13.4732H23.9957Z'
      fill='currentColor'
    />
    <path
      d='M30.2934 23.996C29.6254 21.471 29.7435 19.9597 30.4501 17.173C26.7917 20.7557 25.2915 23.068 24.7628 28.3997C24.8749 32.4349 25.0622 34.6471 28.6116 37.2978C28.7732 35.0846 29.0862 33.8945 30.0364 31.8612C31.3867 33.7131 32.0028 34.6908 32.1335 37.1162C34.9849 35.1659 36.6923 34.1467 36.804 30.2086C36.833 29.1838 35.6568 27.3405 34.1607 25.4263C33.3067 23.5624 33.7573 22.3659 34.6627 20.2152C32.3366 20.7621 31.3453 21.5009 30.2934 23.996Z'
      fill='currentColor'
    />
  </svg>
);

type WaterHeaterUnitProps = {
  waterHeater: WaterHeaterWithMetrics;
  selected: boolean;
  count?: number;
  isLoading?: boolean;
  readOnly?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
};

export const WaterHeaterUnit: React.FC<WaterHeaterUnitProps> = React.memo(
  ({ waterHeater, selected, count, isLoading, actions, readOnly }) => {
    const EnergyIcon = iconForEnergySink(waterHeater.energySink);

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: waterHeater.relativeEnergyDemand,
        costIconCount: waterHeater.relativeCost,
      }),
      [EnergyIcon, waterHeater.relativeEnergyDemand, waterHeater.relativeCost],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          name={waterHeater.name}
          energySource={waterHeater.energySink}
          EnergySourceIcon={EnergyIcon}
          limit={waterHeater.limit}
          coveragePercentage={waterHeater.coveragePercentage}
          energyDemand={Number(waterHeater.energyUsePerYr)}
          capitalCost={Number(waterHeater.capitalCostPerUnit)}
          annualCost={Number(waterHeater.maintenanceCostPerUnit)}
          coverageFieldTitle={'water heater'}
          designLife={waterHeater.designLife}
          isLoading={isLoading}
        />
      ),
      [waterHeater, EnergyIcon, isLoading],
    );

    return (
      <SystemCard
        actions={actions}
        count={count}
        limit={waterHeater.limit}
        name={waterHeater.name}
        selected={selected}
        systemImage={WATER_HEATER_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={isPassiveTechnology(waterHeater)}
      />
    );
  },
);

WaterHeaterUnit.displayName = 'WaterHeaterUnit';
